/**
 * Toast notification system with accessibility
 * - Uses token-based colors (no hardcoded blue/red)
 * - aria-live for screen reader announcements
 * - Keyboard dismiss (Escape) + close button
 *
 * W01d-D1 — generation ownership wired to the REAL identity transition:
 * - every toast captures the identity generation it was added under, read
 *   from the SHARED appIdentityScope at add time (null for pre-bootstrap /
 *   public-surface toasts: no confirmed identity existed — they are
 *   retained across transitions);
 * - every toast captures the identity generation it was added under
 *   (null for public-surface toasts created when NO confirmed identity
 *   exists — the previous identity was already collapsed by an earlier
 *   transition, so a null capture has no departing-generation owner and is
 *   retained);
 * - on a generation TRANSITION, every toast + timer captured under the
 *   departing generation is cleared immediately: the stale toast is
 *   removed and its auto-dismiss timer is cancelled, so identity A's
 *   pending toast AND its timer callback can never surface in identity B;
 * - a notice intentionally created AFTER the transition (logout outcome /
 *   public entry notice — post-generation or null) is retained;
 * - cleanup runs in the provider effect / listener — never a setState
 *   during another component's render.
 */

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
  type KeyboardEvent,
} from 'react';
import { ToastContext } from './ToastContext';
import type { ToastWithGeneration } from './ToastContext';
import { appIdentityScope } from '@/lib/identity-scope';

type ToastType = 'success' | 'error' | 'info';

interface ManagedToast extends ToastWithGeneration {
  /** Auto-dismiss timer id — cleared with the toast on transition. */
  timerId: ReturnType<typeof setTimeout> | null;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ManagedToast[]>([]);
  // Timers are tracked OUTSIDE React state (a ref map) so a transition can
  // clear each departed toast's pending auto-dismiss timer synchronously.
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = Math.random().toString(36).substring(2, 9);
    // Ownership generation: read from the SHARED confirmed-identity authority
    // at dispatch time. null → public/pre-bootstrap surface (retained).
    const generation = appIdentityScope.current().generation;
    const timerId: ReturnType<typeof setTimeout> = setTimeout(() => {
      timersRef.current.delete(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
    timersRef.current.set(id, timerId);
    setToasts((prev) => [...prev, { id, type, message, generation, timerId }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // W01d-D1: on a REAL identity transition (the shared confirmed-authority
  // advance), clear every toast captured under the DEPARTING generation and
  // cancel its pending timer. Toasts captured after the transition
  // (post-transition notice, or pre-bootstrap null generation) are
  // retained. No cross-user/workspace toast leakage: only the departing
  // generation's entries are removed.
  //
  // The clear is deferred to a microtask because the transition advance()
  // can originate from useSyncIdentityScope during another component's
  // render (W01c query seam); reacting synchronously would setState during
  // that render (React warning). A microtask lands after the render
  // commits — visually immediate, never render-phase.
  useEffect(() => {
    const pending = new Set<(() => void)>();
    return appIdentityScope.onInvalidate((prev, next) => {
      if (prev.generation === next.generation) return;
      const departingGeneration = prev.generation;
      const clear = () => {
        setToasts((prevToasts) =>
          prevToasts.filter((t) => {
            if (t.generation !== departingGeneration) return true;
            const timer = timersRef.current.get(t.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(t.id);
            }
            return false;
          }),
        );
      };
      pending.add(clear);
      queueMicrotask(() => {
        pending.delete(clear);
        clear();
      });
    });
  }, []);

  // Global Escape key listener to dismiss all toasts
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && toasts.length > 0) {
        setToasts([]);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toasts.length]);

  // Unmount hygiene: no pending timer outlives the provider.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onRemove }: { toasts: ManagedToast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

/**
 * Token-based toast styling:
 * - success: primary (sky blue) with checkmark
 * - error: warning (amber) with exclamation - not color-only (has icon + text)
 * - info: secondary (violet) with info icon
 */
function getToastStyles(type: ToastType): {
  bg: string;
  text: string;
  icon: string;
  role: 'alert' | 'status';
  ariaLive: 'assertive' | 'polite';
} {
  switch (type) {
    case 'success':
      return {
        bg: 'bg-primary',
        text: 'text-white',
        icon: '✓',
        role: 'status',
        ariaLive: 'polite',
      };
    case 'error':
      return {
        bg: 'bg-warning',
        text: 'text-slate-900',
        icon: '⚠',
        role: 'alert',
        ariaLive: 'assertive',
      };
    case 'info':
      return {
        bg: 'bg-secondary',
        text: 'text-white',
        icon: 'ℹ',
        role: 'status',
        ariaLive: 'polite',
      };
  }
}

function ToastItem({ toast, onRemove }: { toast: ManagedToast; onRemove: (id: string) => void }) {
  const styles = getToastStyles(toast.type);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRemove(toast.id);
    }
  };

  return (
    <div
      className={`${styles.bg} ${styles.text} px-4 py-3 rounded-md shadow-md flex items-center gap-3 min-w-[280px] max-w-md animate-in slide-in-from-right`}
      role={styles.role}
      aria-live={styles.ariaLive}
      aria-atomic="true"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <span className="text-lg flex-shrink-0" aria-hidden="true">
        {styles.icon}
      </span>
      <p className="flex-1 text-sm">
        {toast.type === 'error' && <span className="font-medium">Error: </span>}
        {toast.message}
      </p>
      <button
        onClick={() => onRemove(toast.id)}
        className={`${styles.text} opacity-80 hover:opacity-100 transition-opacity`}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
}
